import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    App,
    Button,
    Card,
    Descriptions,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd'
import type { TableColumnsType } from 'antd'
import {
    DownloadOutlined,
    EyeOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../components/common/FilterBar'
import {
    PaginatedListCard,
    PaginatedListContainer,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../components/common/PaginatedListTable'
import { PageLayout } from '../components/layout'
import { useDedupedMessage } from '../hooks/useDedupedMessage'
import { usePermissions } from '../hooks/usePermissions'
import { localeService } from '../services/localeService'
import {
    getPaymentOrderDetail,
    getPaymentOrderList,
    queryPaymentAttempt,
    queryPaymentRefund,
} from '../services/paymentService'
import type {
    PaymentAttemptSummary,
    PaymentOrderDetail,
    PaymentOrderListItem,
    PaymentProviderQueryResponse,
    PaymentRefundSummary,
} from '../types/payment'
import {
    PAYMENT_DETAIL_ACCESS_PERMISSIONS,
    PAYMENT_QUERY_RESULT_ACCESS_PERMISSIONS,
} from '../utils/permissions'
import { normalizeSearchText } from '../utils/searchText'
import { useTranslation } from 'react-i18next'

const ORDER_STATUS_OPTIONS = [
    'CREATED',
    'PROCESSING',
    'WAITING_PAY',
    'SUCCESS',
    'FAILED',
    'CLOSED',
    'CANCELLED',
    'PART_REFUNDED',
    'REFUNDED',
]

const BUSINESS_TYPE_OPTIONS = ['DEVICE', 'SERVICE', 'PACKAGE', 'TOPUP']
const ALL_FILTER_VALUE = '__ALL__'

interface OrderManagementProps {
    prototypeMode?: boolean
}

type OrderFilters = {
    business_order_no?: string
    business_type?: string
    status?: string
}

const PROTOTYPE_PAYMENT_ORDERS: PaymentOrderListItem[] = [
    {
        business_order_no: 'PAY-20260514-001',
        subject: '灭蚊设备服务套餐',
        business_type: 'PACKAGE',
        business_ref_id: 'PKG-2026-001',
        amount: '1280.00',
        currency: 'CNY',
        status: 'SUCCESS',
        default_provider_code: 'FUIOU',
        created_at: '2026-05-14T09:18:32+08:00',
        updated_at: '2026-05-14T09:22:08+08:00',
        current_attempt: {
            attempt_no: 'ATT-20260514-001',
            provider_code: 'FUIOU',
            provider_method: 'SCAN_QR',
            status: 'SUCCESS',
            created_at: '2026-05-14T09:18:32+08:00',
            updated_at: '2026-05-14T09:22:08+08:00',
            paid_at: '2026-05-14T09:21:42+08:00',
        },
    },
    {
        business_order_no: 'PAY-20260514-002',
        subject: '设备云服务续费',
        business_type: 'SERVICE',
        business_ref_id: 'SVC-2026-014',
        amount: '399.00',
        currency: 'CNY',
        status: 'WAITING_PAY',
        default_provider_code: 'FUIOU',
        created_at: '2026-05-14T10:02:10+08:00',
        updated_at: '2026-05-14T10:02:10+08:00',
        current_attempt: {
            attempt_no: 'ATT-20260514-002',
            provider_code: 'FUIOU',
            provider_method: 'JSAPI',
            status: 'WAITING_PAY',
            created_at: '2026-05-14T10:02:10+08:00',
            updated_at: '2026-05-14T10:02:10+08:00',
        },
    },
    {
        business_order_no: 'PAY-20260513-009',
        subject: '诱蚊设备采购',
        business_type: 'DEVICE',
        business_ref_id: 'DEV-2026-083',
        amount: '2680.00',
        currency: 'CNY',
        status: 'CLOSED',
        default_provider_code: 'FUIOU',
        created_at: '2026-05-13T16:41:25+08:00',
        updated_at: '2026-05-13T18:10:12+08:00',
        current_attempt: null,
    },
]

const normalizeSelectFilter = (value?: string) =>
    value && value !== ALL_FILTER_VALUE ? value : undefined

const filterPrototypeOrders = (
    orders: PaymentOrderListItem[],
    filters: OrderFilters
) => {
    const normalizedOrderNo = normalizeSearchText(
        filters.business_order_no
    ).toLowerCase()

    return orders.filter((order) => {
        const matchesOrderNo = normalizedOrderNo
            ? order.business_order_no.toLowerCase().includes(normalizedOrderNo)
            : true
        const matchesBusinessType = filters.business_type
            ? order.business_type === filters.business_type
            : true
        const matchesStatus = filters.status
            ? order.status === filters.status
            : true

        return matchesOrderNo && matchesBusinessType && matchesStatus
    })
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const statusColorMap: Record<string, string> = {
    CREATED: 'default',
    PROCESSING: 'processing',
    WAITING_PAY: 'gold',
    SUCCESS: 'success',
    FAILED: 'error',
    CLOSED: 'default',
    CANCELLED: 'default',
    PART_REFUNDED: 'cyan',
    REFUNDED: 'blue',
}

const DEFAULT_PAGE_SIZE = 10
const ORDER_TABLE_SCROLL_X = 1520

const actionColumnStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    alignItems: 'center',
    columnGap: 12,
    rowGap: 8,
    maxWidth: '100%',
}

const actionLinkButtonStyle: React.CSSProperties = {
    paddingInline: 4,
    height: 24,
    lineHeight: '24px',
    whiteSpace: 'normal',
}

const OrderManagement: React.FC<OrderManagementProps> = ({
    prototypeMode = false,
}) => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t } = useTranslation(['payment', 'navigation', 'common'])
    const { hasAnyPermissions } = usePermissions()
    const canReadPaymentDetails = hasAnyPermissions(
        PAYMENT_DETAIL_ACCESS_PERMISSIONS
    )
    const canQueryPaymentResult = hasAnyPermissions(
        PAYMENT_QUERY_RESULT_ACCESS_PERMISSIONS
    )
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [items, setItems] = useState<PaymentOrderListItem[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [filters, setFilters] = useState<OrderFilters>({})
    const [detailOpen, setDetailOpen] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [selectedOrderNo, setSelectedOrderNo] = useState<string | null>(null)
    const [detail, setDetail] = useState<PaymentOrderDetail | null>(null)
    const [queryResult, setQueryResult] =
        useState<PaymentProviderQueryResponse | null>(null)
    const [queryLoadingAttemptNo, setQueryLoadingAttemptNo] = useState<
        string | null
    >(null)
    const [queryLoadingRefundNo, setQueryLoadingRefundNo] = useState<
        string | null
    >(null)
    const hasLoadedInitialOrders = useRef(false)

    const loadOrders = useCallback(
        async (
            nextPage: number,
            nextPageSize: number,
            nextFilters: OrderFilters
        ) => {
            setLoading(true)
            try {
                if (prototypeMode) {
                    const filteredOrders = filterPrototypeOrders(
                        PROTOTYPE_PAYMENT_ORDERS,
                        nextFilters
                    )
                    const startIndex = (nextPage - 1) * nextPageSize
                    const pagedOrders = filteredOrders.slice(
                        startIndex,
                        startIndex + nextPageSize
                    )

                    setItems(pagedOrders)
                    setTotal(filteredOrders.length)
                    setPage(nextPage)
                    setPageSize(nextPageSize)
                    return
                }

                const response = await getPaymentOrderList({
                    page: nextPage,
                    page_size: nextPageSize,
                    ...nextFilters,
                })
                setItems(response.items)
                setTotal(response.total)
                setPage(response.page)
                setPageSize(response.page_size)
            } catch (error) {
                console.error('Failed to load payment orders:', error)
                message.error(t('payment:messages.loadFailed'))
            } finally {
                setLoading(false)
            }
        },
        [message, prototypeMode, t]
    )

    const loadDetail = useCallback(
        async (businessOrderNo: string) => {
            setDetailLoading(true)
            try {
                const response = await getPaymentOrderDetail(businessOrderNo)
                setDetail(response)
            } catch (error) {
                console.error('Failed to load payment order detail:', error)
                message.error(t('payment:messages.detailLoadFailed'))
            } finally {
                setDetailLoading(false)
            }
        },
        [message, t]
    )

    useEffect(() => {
        if (hasLoadedInitialOrders.current) {
            return
        }
        hasLoadedInitialOrders.current = true
        void loadOrders(1, DEFAULT_PAGE_SIZE, {})
    }, [loadOrders])

    const handleSearch = async () => {
        const values = form.getFieldsValue()
        const nextFilters = {
            business_order_no:
                normalizeSearchText(values.business_order_no) || undefined,
            business_type: normalizeSelectFilter(values.business_type),
            status: normalizeSelectFilter(values.status),
        }
        setFilters(nextFilters)
        await loadOrders(1, pageSize, nextFilters)
    }

    const handleReset = async () => {
        form.resetFields()
        await loadOrders(1, pageSize, {})
        setFilters({})
    }

    const handleExport = useCallback(async () => {
        setExporting(true)
        try {
            const exportRows = prototypeMode
                ? filterPrototypeOrders(PROTOTYPE_PAYMENT_ORDERS, filters)
                : items

            await new Promise((resolve) => window.setTimeout(resolve, 500))

            if (exportRows.length === 0) {
                message.warning(t('payment:messages.exportEmpty'))
                return
            }

            message.success(t('payment:messages.exportSuccess'))
        } catch (error) {
            console.error('Failed to export payment orders:', error)
            message.error(t('payment:messages.exportFailed'))
        } finally {
            setExporting(false)
        }
    }, [filters, items, message, prototypeMode, t])

    const handleOpenDetail = useCallback(
        async (businessOrderNo: string) => {
            setSelectedOrderNo(businessOrderNo)
            setQueryResult(null)
            setDetailOpen(true)
            await loadDetail(businessOrderNo)
        },
        [loadDetail]
    )

    const refreshCurrentData = useCallback(async () => {
        await loadOrders(page, pageSize, filters)
        if (detailOpen && selectedOrderNo) {
            await loadDetail(selectedOrderNo)
        }
    }, [
        detailOpen,
        filters,
        loadDetail,
        loadOrders,
        page,
        pageSize,
        selectedOrderNo,
    ])

    const handleQueryAttempt = useCallback(
        async (order: PaymentOrderListItem) => {
            const attemptNo = order.current_attempt?.attempt_no
            if (!attemptNo) {
                message.warning(t('payment:messages.noCurrentAttempt'))
                return
            }

            setQueryLoadingAttemptNo(attemptNo)
            try {
                const response = await queryPaymentAttempt(attemptNo)
                setQueryResult(response)
                message.success(t('payment:messages.querySuccess'))
                await refreshCurrentData()
            } catch (error) {
                console.error('Failed to query payment attempt:', error)
                message.error(t('payment:messages.queryFailed'))
            } finally {
                setQueryLoadingAttemptNo(null)
            }
        },
        [message, refreshCurrentData, t]
    )

    const handleQueryRefund = useCallback(
        async (refund: PaymentRefundSummary) => {
            setQueryLoadingRefundNo(refund.refund_order_no)
            try {
                const response = await queryPaymentRefund(
                    refund.refund_order_no
                )
                setQueryResult(response)
                message.success(t('payment:messages.refundQuerySuccess'))
                await refreshCurrentData()
            } catch (error) {
                console.error('Failed to query payment refund:', error)
                message.error(t('payment:messages.refundQueryFailed'))
            } finally {
                setQueryLoadingRefundNo(null)
            }
        },
        [message, refreshCurrentData, t]
    )

    const formatDateTime = useCallback((value?: string | null) => {
        return value ? localeService.formatDate(value) : '-'
    }, [])

    const formatPaymentMethod = useCallback(
        (value?: string | null) =>
            value
                ? t(`payment:paymentMethod.${value}`, { defaultValue: value })
                : '-',
        [t]
    )

    const formatBusinessType = useCallback(
        (value?: string | null) =>
            value
                ? t(`payment:businessType.${value}`, { defaultValue: value })
                : '-',
        [t]
    )

    const formatBusinessRefType = useCallback(
        (value?: string | null) => {
            if (!value) {
                return '-'
            }
            return t(`payment:businessRefType.${value}`, {
                defaultValue: t(`payment:businessType.${value}`, {
                    defaultValue: value,
                }),
            })
        },
        [t]
    )

    const formatTradeType = useCallback(
        (value?: string | null) =>
            value
                ? t(`payment:tradeType.${value}`, { defaultValue: value })
                : '-',
        [t]
    )

    const formatAmountWithUnit = useCallback(
        (amount?: string | null, currency?: string | null) => {
            if (!amount) {
                return '-'
            }
            if (!currency) {
                return amount
            }
            return `${amount} ${t(`payment:currencyUnit.${currency}`, {
                defaultValue: currency,
            })}`
        },
        [t]
    )

    const formatStatusTag = useCallback(
        (value?: string | null) =>
            value ? (
                <Tag color={statusColorMap[value] || 'default'}>
                    {t(`payment:status.${value}`, { defaultValue: value })}
                </Tag>
            ) : (
                '-'
            ),
        [t]
    )

    const formatProviderState = useCallback(
        (value?: string | null) =>
            value
                ? t(`payment:providerState.${value}`, { defaultValue: value })
                : '-',
        [t]
    )

    const formatFallbackValue = useCallback(
        (value: unknown) => {
            if (value === null || value === undefined || value === '') {
                return '-'
            }
            if (typeof value === 'boolean') {
                return value ? t('common:common.yes') : t('common:common.no')
            }
            if (typeof value === 'number' || typeof value === 'string') {
                return String(value)
            }
            return String(value)
        },
        [t]
    )

    const renderReadableValue = useCallback(
        (value: unknown): React.ReactNode => {
            if (value === null || value === undefined || value === '') {
                return '-'
            }
            if (Array.isArray(value)) {
                if (value.length === 0) {
                    return t('payment:detail.emptyInfo')
                }
                return (
                    <Space direction="vertical" size={4}>
                        {value.map((item, index) => (
                            <Typography.Text key={index}>
                                {isRecord(item)
                                    ? Object.entries(item)
                                          .map(
                                              ([key, itemValue]) =>
                                                  `${key}: ${formatFallbackValue(itemValue)}`
                                          )
                                          .join(' / ')
                                    : formatFallbackValue(item)}
                            </Typography.Text>
                        ))}
                    </Space>
                )
            }
            if (isRecord(value)) {
                return (
                    <Descriptions column={1} size="small" bordered>
                        {Object.entries(value).map(([key, itemValue]) => (
                            <Descriptions.Item key={key} label={key}>
                                {renderReadableValue(itemValue)}
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                )
            }
            return formatFallbackValue(value)
        },
        [formatFallbackValue, t]
    )

    const renderKeyValueDetails = useCallback(
        (record?: Record<string, unknown> | null) => {
            if (!record || Object.keys(record).length === 0) {
                return (
                    <Typography.Text type="secondary">
                        {t('payment:detail.emptyInfo')}
                    </Typography.Text>
                )
            }

            return (
                <Descriptions column={1} size="small" bordered>
                    {Object.entries(record).map(([key, value]) => (
                        <Descriptions.Item key={key} label={key}>
                            {renderReadableValue(value)}
                        </Descriptions.Item>
                    ))}
                </Descriptions>
            )
        },
        [renderReadableValue, t]
    )

    const attemptColumns = useMemo<TableColumnsType<PaymentAttemptSummary>>(
        () => [
            {
                title: t('payment:attempt.attemptNo'),
                dataIndex: 'attempt_no',
                key: 'attempt_no',
            },
            {
                title: t('payment:attempt.paymentMethod'),
                dataIndex: 'provider_method',
                key: 'provider_method',
                render: (value?: string | null) => formatPaymentMethod(value),
            },
            {
                title: t('payment:attempt.status'),
                dataIndex: 'status',
                key: 'status',
                render: (value?: string | null) => formatStatusTag(value),
            },
            {
                title: t('payment:attempt.providerState'),
                dataIndex: 'provider_state',
                key: 'provider_state',
                render: (value?: string | null) => formatProviderState(value),
            },
            {
                title: t('payment:attempt.providerTraceNo'),
                dataIndex: 'provider_trace_no',
                key: 'provider_trace_no',
                render: (value?: string | null) => value || '-',
            },
            {
                title: t('payment:attempt.createdAt'),
                dataIndex: 'created_at',
                key: 'created_at',
                render: (value?: string | null) => formatDateTime(value),
            },
            {
                title: t('payment:attempt.updatedAt'),
                dataIndex: 'updated_at',
                key: 'updated_at',
                render: (value?: string | null) => formatDateTime(value),
            },
        ],
        [
            formatDateTime,
            formatPaymentMethod,
            formatProviderState,
            formatStatusTag,
            t,
        ]
    )

    const refundColumns = useMemo<TableColumnsType<PaymentRefundSummary>>(
        () => [
            {
                title: t('payment:refund.refundOrderNo'),
                dataIndex: 'refund_order_no',
                key: 'refund_order_no',
            },
            {
                title: t('payment:refund.amount'),
                key: 'amount',
                render: (_, record) => record.amount || '-',
            },
            {
                title: t('payment:refund.status'),
                dataIndex: 'status',
                key: 'status',
                render: (value?: string | null) => formatStatusTag(value),
            },
            {
                title: t('payment:refund.providerState'),
                dataIndex: 'provider_state',
                key: 'provider_state',
                render: (value?: string | null) => formatProviderState(value),
            },
            {
                title: t('payment:refund.providerRefundId'),
                dataIndex: 'provider_refund_id',
                key: 'provider_refund_id',
                render: (value?: string | null) => value || '-',
            },
            {
                title: t('payment:refund.refundedAt'),
                dataIndex: 'refunded_at',
                key: 'refunded_at',
                render: (value?: string | null) => formatDateTime(value),
            },
            {
                title: t('payment:refund.createdAt'),
                dataIndex: 'created_at',
                key: 'created_at',
                render: (value?: string | null) => formatDateTime(value),
            },
            {
                title: t('payment:table.actions'),
                key: 'actions',
                render: (_, record) =>
                    canQueryPaymentResult ? (
                        <Button
                            icon={<SearchOutlined aria-hidden />}
                            loading={
                                queryLoadingRefundNo === record.refund_order_no
                            }
                            onClick={() => void handleQueryRefund(record)}
                        >
                            {t('payment:actions.viewRefundResult')}
                        </Button>
                    ) : null,
            },
        ],
        [
            canQueryPaymentResult,
            formatDateTime,
            formatProviderState,
            formatStatusTag,
            handleQueryRefund,
            queryLoadingRefundNo,
            t,
        ]
    )

    const itemColumns = useMemo<TableColumnsType<Record<string, unknown>>>(
        () => [
            {
                title: t('payment:item.name'),
                dataIndex: 'name',
                key: 'name',
                render: formatFallbackValue,
            },
            {
                title: t('payment:item.spec'),
                dataIndex: 'spec',
                key: 'spec',
                render: formatFallbackValue,
            },
            {
                title: t('payment:item.quantity'),
                dataIndex: 'quantity',
                key: 'quantity',
                render: formatFallbackValue,
            },
            {
                title: t('payment:item.unitPrice'),
                dataIndex: 'unit_price',
                key: 'unit_price',
                render: formatFallbackValue,
            },
            {
                title: t('payment:item.originalPrice'),
                dataIndex: 'original_price',
                key: 'original_price',
                render: formatFallbackValue,
            },
            {
                title: t('payment:item.discountAmount'),
                dataIndex: 'discount_amount',
                key: 'discount_amount',
                render: formatFallbackValue,
            },
            {
                title: t('payment:item.displaySummary'),
                dataIndex: 'display_summary',
                key: 'display_summary',
                render: formatFallbackValue,
            },
        ],
        [formatFallbackValue, t]
    )

    const columns = useMemo<TableColumnsType<PaymentOrderListItem>>(
        () => [
            {
                title: t('payment:table.orderNo'),
                dataIndex: 'business_order_no',
                key: 'business_order_no',
                width: 190,
                ellipsis: true,
                render: renderPaginatedListText,
            },
            {
                title: t('payment:table.subject'),
                dataIndex: 'subject',
                key: 'subject',
                width: 190,
                ellipsis: true,
                render: renderPaginatedListText,
            },
            {
                title: t('payment:table.businessType'),
                dataIndex: 'business_type',
                key: 'business_type',
                width: 120,
                render: (value?: string | null) => formatBusinessType(value),
            },
            {
                title: t('payment:table.businessRefId'),
                dataIndex: 'business_ref_id',
                key: 'business_ref_id',
                width: 190,
                ellipsis: true,
                render: renderPaginatedListText,
            },
            {
                title: t('payment:table.amount'),
                key: 'amount',
                width: 140,
                render: (_, record) =>
                    formatAmountWithUnit(record.amount, record.currency),
            },
            {
                title: t('payment:table.status'),
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (value: string) => (
                    <Tag color={statusColorMap[value] || 'default'}>
                        {t(`payment:status.${value}`, { defaultValue: value })}
                    </Tag>
                ),
            },
            {
                title: t('payment:table.paymentMethod'),
                key: 'payment_method',
                width: 180,
                render: (_, record) =>
                    record.current_attempt
                        ? formatPaymentMethod(
                              record.current_attempt.provider_method
                          )
                        : t('payment:table.noCurrentAttempt'),
            },
            {
                title: t('payment:table.createdAt'),
                dataIndex: 'created_at',
                key: 'created_at',
                width: 230,
                render: (value: string) =>
                    localeService.formatDate(value, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                    }),
            },
            {
                title: t('payment:table.actions'),
                key: 'actions',
                width: 240,
                fixed: 'right' as const,
                render: (_, record) => (
                    <div style={actionColumnStyle}>
                        {canReadPaymentDetails ? (
                            <Button
                                type="link"
                                icon={<EyeOutlined aria-hidden />}
                                onClick={() =>
                                    void handleOpenDetail(
                                        record.business_order_no
                                    )
                                }
                                style={actionLinkButtonStyle}
                            >
                                {t('payment:actions.viewDetail')}
                            </Button>
                        ) : null}
                        {canQueryPaymentResult ? (
                            <Button
                                type="link"
                                icon={<SearchOutlined aria-hidden />}
                                loading={
                                    queryLoadingAttemptNo ===
                                    record.current_attempt?.attempt_no
                                }
                                disabled={!record.current_attempt}
                                onClick={() => void handleQueryAttempt(record)}
                                title={
                                    record.current_attempt
                                        ? undefined
                                        : t('payment:messages.noCurrentAttempt')
                                }
                                style={actionLinkButtonStyle}
                            >
                                {t('payment:actions.viewPaymentResult')}
                            </Button>
                        ) : null}
                    </div>
                ),
            },
        ],
        [
            canQueryPaymentResult,
            canReadPaymentDetails,
            formatAmountWithUnit,
            formatBusinessType,
            formatPaymentMethod,
            handleOpenDetail,
            handleQueryAttempt,
            queryLoadingAttemptNo,
            t,
        ]
    )

    return (
        <PageLayout
            title={t('payment:title')}
            breadcrumb={[
                {
                    title: t('navigation:breadcrumb.paymentCenter'),
                },
                {
                    title: t('navigation:breadcrumb.orderManagement'),
                    path: '/orders',
                },
            ]}
        >
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Form
                    form={form}
                    initialValues={{
                        business_type: ALL_FILTER_VALUE,
                        status: ALL_FILTER_VALUE,
                    }}
                >
                    <FilterBar>
                        <FilterField label={t('payment:filters.orderNo')}>
                            <Form.Item name="business_order_no" noStyle>
                                <Input
                                    placeholder={t(
                                        'payment:filters.orderNoPlaceholder'
                                    )}
                                />
                            </Form.Item>
                        </FilterField>
                        <FilterField label={t('payment:filters.businessType')}>
                            <Form.Item name="business_type" noStyle>
                                <Select
                                    allowClear
                                    options={[
                                        {
                                            label: t(
                                                'payment:filters.allBusinessTypes'
                                            ),
                                            value: ALL_FILTER_VALUE,
                                        },
                                        ...BUSINESS_TYPE_OPTIONS.map(
                                            (value) => ({
                                                label: t(
                                                    `payment:businessType.${value}`,
                                                    { defaultValue: value }
                                                ),
                                                value,
                                            })
                                        ),
                                    ]}
                                />
                            </Form.Item>
                        </FilterField>
                        <FilterField label={t('payment:filters.status')}>
                            <Form.Item name="status" noStyle>
                                <Select
                                    allowClear
                                    options={[
                                        {
                                            label: t(
                                                'payment:filters.allStatuses'
                                            ),
                                            value: ALL_FILTER_VALUE,
                                        },
                                        ...ORDER_STATUS_OPTIONS.map(
                                            (value) => ({
                                                label: t(
                                                    `payment:status.${value}`,
                                                    { defaultValue: value }
                                                ),
                                                value,
                                            })
                                        ),
                                    ]}
                                />
                            </Form.Item>
                        </FilterField>
                        <FilterActions>
                            <Button
                                type="primary"
                                onClick={() => void handleSearch()}
                            >
                                {t('payment:actions.search')}
                            </Button>
                            <Button onClick={() => void handleReset()}>
                                {t('payment:actions.reset')}
                            </Button>
                        </FilterActions>
                    </FilterBar>
                </Form>

                <PaginatedListContainer>
                    <PaginatedListToolbar>
                        <Button
                            icon={<ReloadOutlined />}
                            loading={loading}
                            onClick={() => void refreshCurrentData()}
                        >
                            {t('common:buttons.refresh')}
                        </Button>
                        {prototypeMode ? (
                            <Button
                                icon={<DownloadOutlined />}
                                loading={exporting}
                                onClick={() => void handleExport()}
                            >
                                {t('payment:actions.export')}
                            </Button>
                        ) : null}
                    </PaginatedListToolbar>

                    <PaginatedListCard>
                        <Table<PaymentOrderListItem>
                            rowKey="business_order_no"
                            loading={loading}
                            columns={columns}
                            dataSource={items}
                            size={PAGINATED_LIST_TABLE_SIZE}
                            tableLayout="fixed"
                            scroll={{ x: ORDER_TABLE_SCROLL_X }}
                            pagination={buildPaginatedListPagination({
                                current: page,
                                pageSize,
                                total,
                                showTotal: (count) =>
                                    t('payment:pagination.totalOrders', {
                                        total: count,
                                    }),
                                onChange: (nextPage, nextPageSize) => {
                                    const resolvedPageSize =
                                        nextPageSize ?? pageSize
                                    void loadOrders(
                                        resolvedPageSize !== pageSize
                                            ? 1
                                            : nextPage,
                                        resolvedPageSize,
                                        filters
                                    )
                                },
                            })}
                        />
                    </PaginatedListCard>
                </PaginatedListContainer>

                <Modal
                    title={t('payment:detail.title')}
                    width={720}
                    open={detailOpen}
                    centered
                    footer={null}
                    maskClosable={false}
                    styles={{
                        body: {
                            maxHeight: '70vh',
                            overflowY: 'auto',
                        },
                    }}
                    onCancel={() => {
                        setDetailOpen(false)
                        setDetail(null)
                        setSelectedOrderNo(null)
                        setQueryResult(null)
                        setQueryLoadingRefundNo(null)
                    }}
                >
                    {detailLoading ? (
                        <Typography.Text>
                            {t('common:messages.loading')}
                        </Typography.Text>
                    ) : detail ? (
                        <Space
                            direction="vertical"
                            size="large"
                            style={{ width: '100%' }}
                        >
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item
                                    label={t('payment:detail.orderNo')}
                                >
                                    {detail.business_order_no}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.subject')}
                                >
                                    {detail.subject}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.businessType')}
                                >
                                    {formatBusinessType(detail.business_type)}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.businessRefType')}
                                >
                                    {formatBusinessRefType(
                                        detail.business_ref_type
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.businessRefId')}
                                >
                                    {detail.business_ref_id}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.amount')}
                                >
                                    {formatAmountWithUnit(
                                        detail.amount,
                                        detail.currency
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.status')}
                                >
                                    <Tag
                                        color={
                                            statusColorMap[detail.status] ||
                                            'default'
                                        }
                                    >
                                        {t(`payment:status.${detail.status}`, {
                                            defaultValue: detail.status,
                                        })}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.description')}
                                >
                                    {detail.description || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.createdAt')}
                                >
                                    {localeService.formatDate(
                                        detail.created_at
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.updatedAt')}
                                >
                                    {localeService.formatDate(
                                        detail.updated_at
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.paidAt')}
                                >
                                    {detail.paid_at
                                        ? localeService.formatDate(
                                              detail.paid_at
                                          )
                                        : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item
                                    label={t('payment:detail.closedAt')}
                                >
                                    {detail.closed_at
                                        ? localeService.formatDate(
                                              detail.closed_at
                                          )
                                        : '-'}
                                </Descriptions.Item>
                            </Descriptions>

                            {queryResult ? (
                                <Alert
                                    type={
                                        queryResult.success
                                            ? 'success'
                                            : 'warning'
                                    }
                                    message={t('payment:queryResult.title')}
                                    description={
                                        <Space
                                            direction="vertical"
                                            style={{ width: '100%' }}
                                        >
                                            <Typography.Text>
                                                {t(
                                                    'payment:queryResult.statusLabel'
                                                )}{' '}
                                                {t(
                                                    `payment:status.${queryResult.status}`,
                                                    {
                                                        defaultValue:
                                                            queryResult.status,
                                                    }
                                                )}
                                            </Typography.Text>
                                            <Typography.Text>
                                                {t(
                                                    'payment:queryResult.providerStateLabel'
                                                )}{' '}
                                                {formatProviderState(
                                                    queryResult.provider_state
                                                )}
                                            </Typography.Text>
                                            <Card
                                                size="small"
                                                title={t(
                                                    'payment:queryResult.providerResponse'
                                                )}
                                            >
                                                {renderKeyValueDetails(
                                                    queryResult.provider_response
                                                )}
                                            </Card>
                                        </Space>
                                    }
                                />
                            ) : null}

                            <Card title={t('payment:detail.currentAttempt')}>
                                {detail.current_attempt ? (
                                    <Space
                                        direction="vertical"
                                        size="middle"
                                        style={{ width: '100%' }}
                                    >
                                        <Descriptions
                                            bordered
                                            column={1}
                                            size="small"
                                        >
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.paymentMethod'
                                                )}
                                            >
                                                {formatPaymentMethod(
                                                    detail.current_attempt
                                                        .provider_method
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.tradeType'
                                                )}
                                            >
                                                {formatTradeType(
                                                    detail.current_attempt
                                                        .provider_trade_type
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.status'
                                                )}
                                            >
                                                {formatStatusTag(
                                                    detail.current_attempt
                                                        .status
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.providerState'
                                                )}
                                            >
                                                {formatProviderState(
                                                    detail.current_attempt
                                                        .provider_state
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.providerTraceNo'
                                                )}
                                            >
                                                {detail.current_attempt
                                                    .provider_trace_no || '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.providerOrderNo'
                                                )}
                                            >
                                                {detail.current_attempt
                                                    .provider_order_no || '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.createdAt'
                                                )}
                                            >
                                                {formatDateTime(
                                                    detail.current_attempt
                                                        .created_at
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.updatedAt'
                                                )}
                                            >
                                                {formatDateTime(
                                                    detail.current_attempt
                                                        .updated_at
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.paidAt'
                                                )}
                                            >
                                                {formatDateTime(
                                                    detail.current_attempt
                                                        .paid_at
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.closedAt'
                                                )}
                                            >
                                                {formatDateTime(
                                                    detail.current_attempt
                                                        .closed_at
                                                )}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t(
                                                    'payment:attempt.expiresAt'
                                                )}
                                            >
                                                {formatDateTime(
                                                    detail.current_attempt
                                                        .expires_at
                                                )}
                                            </Descriptions.Item>
                                        </Descriptions>
                                        {detail.current_attempt.pay_info ? (
                                            <Card
                                                size="small"
                                                title={t(
                                                    'payment:attempt.payInfo'
                                                )}
                                            >
                                                {renderKeyValueDetails(
                                                    detail.current_attempt
                                                        .pay_info
                                                )}
                                            </Card>
                                        ) : null}
                                    </Space>
                                ) : (
                                    <Typography.Text type="secondary">
                                        {t('payment:detail.noCurrentAttempt')}
                                    </Typography.Text>
                                )}
                            </Card>

                            <Card title={t('payment:detail.attempts')}>
                                <Table<PaymentAttemptSummary>
                                    rowKey="attempt_no"
                                    columns={attemptColumns}
                                    dataSource={detail.attempts}
                                    pagination={false}
                                    size="small"
                                    locale={{
                                        emptyText: t(
                                            'payment:detail.noAttempts'
                                        ),
                                    }}
                                />
                            </Card>

                            <Card title={t('payment:detail.refunds')}>
                                <Table<PaymentRefundSummary>
                                    rowKey="refund_order_no"
                                    columns={refundColumns}
                                    dataSource={detail.refunds}
                                    pagination={false}
                                    size="small"
                                    locale={{
                                        emptyText: t(
                                            'payment:detail.noRefunds'
                                        ),
                                    }}
                                />
                            </Card>

                            <Card title={t('payment:detail.itemSnapshot')}>
                                <Table<Record<string, unknown>>
                                    rowKey={(_, index) => String(index)}
                                    columns={itemColumns}
                                    dataSource={detail.item_snapshot}
                                    pagination={false}
                                    size="small"
                                    locale={{
                                        emptyText: t('payment:detail.noItems'),
                                    }}
                                />
                            </Card>

                            <Card title={t('payment:detail.bizSnapshot')}>
                                {renderKeyValueDetails(detail.biz_snapshot)}
                            </Card>

                            <Card title={t('payment:detail.metadata')}>
                                {renderKeyValueDetails(detail.metadata)}
                            </Card>
                        </Space>
                    ) : (
                        <Typography.Text>
                            {t('payment:messages.detailEmpty')}
                        </Typography.Text>
                    )}
                </Modal>
            </Space>
        </PageLayout>
    )
}

export default OrderManagement
